import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import "./CustomCalendar.scss";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const CustomCalendar = ({ value, onChange, className = "" }) => {
    const selected = value ? moment(value) : moment();
    const [viewDate, setViewDate] = useState(() => selected.clone().startOf("month"));

    useEffect(() => {
        if (value) {
            setViewDate(moment(value).startOf("month"));
        }
    }, [value]);

    const weeks = useMemo(() => {
        const start = viewDate.clone().startOf("month").startOf("week");
        const end = viewDate.clone().endOf("month").endOf("week");
        const days = [];
        const cursor = start.clone();

        while (cursor.isSameOrBefore(end, "day")) {
            days.push(cursor.clone());
            cursor.add(1, "day");
        }

        const rows = [];
        for (let i = 0; i < days.length; i += 7) {
            rows.push(days.slice(i, i + 7));
        }
        return rows;
    }, [viewDate]);

    const handleSelect = (day) => {
        onChange(day.toDate());
    };

    return (
        <div className={`custom-calendar ${className}`.trim()} role="dialog" aria-label="Choose date">
            <div className="custom-calendar__year-nav">
                <button
                    type="button"
                    className="custom-calendar__nav-btn"
                    aria-label="Previous year"
                    onClick={() => setViewDate((prev) => prev.clone().subtract(1, "year"))}
                >
                    ‹
                </button>
                <span className="custom-calendar__year">{viewDate.format("YYYY")}</span>
                <button
                    type="button"
                    className="custom-calendar__nav-btn"
                    aria-label="Next year"
                    onClick={() => setViewDate((prev) => prev.clone().add(1, "year"))}
                >
                    ›
                </button>
            </div>

            <div className="custom-calendar__month-nav">
                <button
                    type="button"
                    className="custom-calendar__nav-btn"
                    aria-label="Previous month"
                    onClick={() => setViewDate((prev) => prev.clone().subtract(1, "month"))}
                >
                    ‹
                </button>
                <span className="custom-calendar__month">{viewDate.format("MMMM")}</span>
                <button
                    type="button"
                    className="custom-calendar__nav-btn"
                    aria-label="Next month"
                    onClick={() => setViewDate((prev) => prev.clone().add(1, "month"))}
                >
                    ›
                </button>
            </div>

            <div className="custom-calendar__weekdays">
                {WEEKDAYS.map((day) => (
                    <span key={day} className="custom-calendar__weekday">
                        {day}
                    </span>
                ))}
            </div>

            <div className="custom-calendar__grid">
                {weeks.map((week, rowIndex) => (
                    <div key={`week-${rowIndex}`} className="custom-calendar__row">
                        {week.map((day) => {
                            const isCurrentMonth = day.month() === viewDate.month();
                            const isSelected = value && day.isSame(moment(value), "day");
                            const isToday = day.isSame(moment(), "day");

                            return (
                                <button
                                    key={day.format("YYYY-MM-DD")}
                                    type="button"
                                    className={[
                                        "custom-calendar__day",
                                        !isCurrentMonth && "custom-calendar__day--muted",
                                        isSelected && "custom-calendar__day--selected",
                                        isToday && !isSelected && "custom-calendar__day--today",
                                    ]
                                        .filter(Boolean)
                                        .join(" ")}
                                    onClick={() => handleSelect(day)}
                                >
                                    {day.date()}
                                </button>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CustomCalendar;
